import React, { useEffect, useMemo, useState } from 'react';
import { AlarmClockIcon, CheckIcon, LockKeyholeIcon, ShieldCheckIcon, ZapIcon } from 'lucide-react';
import { PrimaryButton } from './PrimaryButton';
const checkout = 'https://esl-teacher-ready.impultienda.ar/checkout';
const includes = ['120 Ready-to-Teach English Lessons', '6 color-coded progressive volumes from A1 to B1', 'Speaking, reading, writing, vocabulary, grammar, and listening activities', 'Teacher instructions, assessments, answer keys, and homework options', '100 speaking activities + 30 emergency lessons', '5 premium strategic bonuses', '7-day money-back guarantee'];
const bonuses = [['52 Weekly ESL Teaching Themes', 'Keep every week meaningful and fresh without planning from zero.'], ['Ultimate ESL Instruction Language Library', 'Clear language for instructions, explanations, corrections, and encouragement.'], ['ESL Beginner Support Guide', 'Practical support strategies for students starting from zero.'], ['30 Emergency ESL Lessons', 'A no-prep answer for unexpected or substitute teaching days.'], ['ESL Lesson Finder System', 'Choose the right lesson for any class in under 60 seconds.']];
function useCountdown() {
  const target = useMemo(() => Date.now() + 24 * 60 * 60 * 1000, []);
  const [remaining, setRemaining] = useState(target - Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setRemaining(Math.max(0, target - Date.now())), 1000);
    return () => window.clearInterval(timer);
  }, [target]);
  return [Math.floor(remaining / 3600000), Math.floor(remaining % 3600000 / 60000), Math.floor(remaining % 60000 / 1000)] as const;
}
export function OfferSection() {
  const [hours, minutes, seconds] = useCountdown();
  return <>
      <section className="bg-cream px-4 py-20 sm:py-28" data-id="element-160">
        <div className="mx-auto max-w-6xl" data-id="element-161">
          <div className="text-center" data-id="element-162">
            <div className="section-rule mx-auto" data-id="element-163" />
            <p className="mt-5 text-xs font-extrabold tracking-[.16em] text-gold-500" data-id="element-164">
              MORE VALUE, ZERO EXTRA COST
            </p>
            <h2 className="mt-4 font-display text-3xl uppercase text-navy-950 sm:text-5xl" data-id="element-165">
              Five strategic bonuses{' '}
              <span className="text-gold-500" data-id="element-166">included free.</span>
            </h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-167">
            {bonuses.map(([title, text], index) => <article key={title} className="relative rounded-2xl border border-[#e6d9b7] bg-white p-6 shadow-card" data-id="element-168">
                <span className="absolute right-4 top-4 rounded-full bg-gold-100 px-3 py-1 text-[10px] font-extrabold tracking-wider text-gold-700" data-id="element-169">
                  BONUS 0{index + 1}
                </span>
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-navy-900 text-lg font-black text-gold-300" data-id="element-170">
                  +
                </span>
                <h3 className="mt-5 pr-12 text-lg font-extrabold text-navy-950" data-id="element-171">
                  {title}
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-600" data-id="element-172">{text}</p>
                <p className="mt-5 text-sm font-extrabold text-emerald-600" data-id="element-173">
                  FREE with your system
                </p>
              </article>)}
          </div>
        </div>
      </section>

      <section id="offer" className="relative overflow-hidden bg-navy-950 px-4 py-20 text-white sm:py-28" data-id="element-174">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(29,79,140,.7),transparent_34%)]" data-id="element-175" />
        <div className="relative mx-auto max-w-4xl" data-id="element-176">
          <div className="text-center" data-id="element-177">
            <p className="text-xs font-extrabold tracking-[.16em] text-gold-300" data-id="element-178">
              LIMITED LAUNCH OFFER
            </p>
            <h2 className="mt-4 font-display text-3xl uppercase leading-tight sm:text-5xl" data-id="element-179">
              Get the complete ESL system{' '}
              <span className="text-gold-300" data-id="element-180">today.</span>
            </h2>
          </div>
          <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_.85fr]" data-id="element-181">
            <div className="rounded-3xl border border-white/15 bg-white p-7 text-navy-950 shadow-card sm:p-10" data-id="element-182">
              <span className="inline-flex rounded-full bg-gold-300 px-3 py-1 text-xs font-extrabold" data-id="element-183">
                COMPLETE 6-IN-1 PACK
              </span>
              <h3 className="mt-5 font-display text-3xl uppercase leading-none" data-id="element-184">
                ESL Teacher Ready
              </h3>
              <p className="mt-4 text-sm leading-6 text-slate-600" data-id="element-185">
                Everything you need to plan less, teach with confidence, and
                keep every lesson fresh.
              </p>
              <ul className="mt-6 space-y-3" data-id="element-186">
                {includes.map(item => <li key={item} className="flex gap-3 text-sm font-semibold text-slate-700" data-id="element-187">
                    <CheckIcon size={18} className="mt-0.5 shrink-0 text-emerald-500" data-id="element-188" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div className="rounded-3xl border-2 border-gold-300 bg-navy-800 p-7 text-center shadow-gold sm:p-10" data-id="element-189">
              <p className="text-sm font-bold text-blue-100" data-id="element-190">
                ONE-TIME LAUNCH PRICE
              </p>
              <p className="mt-4 text-xl font-bold text-blue-200 line-through" data-id="element-191">
                USD 54.00
              </p>
              <p className="mt-1 font-display text-5xl text-gold-300" data-id="element-192">USD 27</p>
              <p className="mt-2 text-sm font-semibold text-gold-100" data-id="element-193">
                50% OFF — TODAY ONLY
              </p>
              <PrimaryButton href={checkout} className="mt-7 w-full" data-id="element-194">
                YES! I WANT 120 READY-TO-TEACH LESSONS
              </PrimaryButton>
              <p className="mt-4 text-xs text-blue-100" data-id="element-195">
                Instant digital access • Secure payment • 7-day guarantee
              </p>
              <div className="mt-7 border-t border-white/15 pt-6 text-left text-xs text-blue-100" data-id="element-196">
                <p className="flex gap-2" data-id="element-197">
                  <LockKeyholeIcon size={15} className="text-gold-300" data-id="element-198" /> Secure
                  checkout in your local currency
                </p>
                <p className="mt-3 flex gap-2" data-id="element-199">
                  <ZapIcon size={15} className="text-gold-300" data-id="element-200" /> Start teaching
                  with confidence today
                </p>
                <p className="mt-3 flex gap-2" data-id="element-201">
                  <ShieldCheckIcon size={15} className="text-gold-300" data-id="element-202" />{' '}
                  Protected by a 7-day guarantee
                </p>
              </div>
            </div>
          </div>
          <div className="mx-auto mt-10 max-w-3xl rounded-2xl border border-gold-300/50 bg-white/10 p-5 text-center" data-id="element-203">
            <p className="inline-flex items-center gap-2 text-sm font-extrabold text-gold-100" data-id="element-204">
              <AlarmClockIcon size={18} data-id="element-205" /> Launch price expires in
            </p>
            <div className="mt-4 flex justify-center gap-3" data-id="element-206">
              <TimeBox value={hours} label="HRS" data-id="element-207" />
              <TimeBox value={minutes} label="MIN" data-id="element-208" />
              <TimeBox value={seconds} label="SEC" data-id="element-209" />
            </div>
          </div>
        </div>
      </section>
    </>;
}
function TimeBox({
  value,
  label
}: {
  value: number;
  label: string;
}) {
  return <div className="rounded-xl bg-white px-4 py-3 text-navy-950" data-id="element-210">
      <span className="block text-2xl font-black tabular-nums" data-id="element-211">
        {String(value).padStart(2, '0')}
      </span>
      <span className="text-[10px] font-extrabold tracking-wider text-navy-700" data-id="element-212">
        {label}
      </span>
    </div>;
}